/**
 * Toutes les valeurs de réglage, en un seul endroit.
 *
 * Chacune porte la raison de son existence. Ce sont ces nombres qui font la
 * différence entre un conseil utile et une fausse alerte, et il faut pouvoir
 * les relire dans un an sans redécouvrir pourquoi ils valent cela.
 */

/**
 * Clés de stockage local.
 *
 * `position` et `nose` gardent leur nom d'origine : ce sont des données de
 * l'utilisateur, et une migration à la lecture suffit à absorber l'ancien
 * format. `cache` et `zones` sont suffixés, car leur forme a changé et qu'il
 * s'agit de données éphémères : les jeter coûte un rechargement, rien de plus.
 */
export const STORAGE_KEYS = {
  position: 'if3.pos',
  nose: 'if3.nose',
  cache: 'if3.cache.v2',
  zones: 'if3.zones.v2',
} as const;

/** Centre de la France, avant que l'utilisateur ait choisi un endroit. */
export const FALLBACK_POSITION = { lat: 46.6, lon: 2.4, label: 'France' } as const;

/* ---------------------------------------------------------------------------
   Fraîcheur des données

   Une donnée trop vieille cesse de décider. Passé ces délais, l'application
   affiche "pas d'info" plutôt qu'un chiffre périmé : c'est le seul
   comportement honnête quand on conseille d'ouvrir ou de fermer une fenêtre.
--------------------------------------------------------------------------- */

export const SENSOR_RADIUS_KM = 25;
/** Au-delà, une mesure de capteur ne décide plus. */
export const SENSOR_MAX_AGE_MIN = 60;
/** Au-delà, une estimation du modèle ne décide plus. */
export const MODEL_MAX_AGE_MIN = 180;
/** Au-delà, on n'affiche plus le dernier balayage satellite mémorisé. */
export const ZONES_MAX_AGE_MIN = 180;
/** Un signalement d'odeur reste prioritaire pendant cette durée. */
export const NOSE_MAX_AGE_MIN = 30;
/** Nombre d'entrées conservées dans le journal des odeurs. */
export const NOSE_HISTORY_MAX = 300;

/* ---------------------------------------------------------------------------
   Réseau
--------------------------------------------------------------------------- */

export const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
/** Ré-affichage périodique, pour que les heures d'arrivée restent justes. */
export const RERENDER_INTERVAL_MS = 60 * 1000;
export const REQUEST_TIMEOUT_MS = 12_000;
export const REQUEST_TRIES = 3;

/* ---------------------------------------------------------------------------
   Modèle de panache

   Un panache ne s'évase pas en cône large : il s'élargit lentement, de
   l'ordre de 10° de demi-angle, avec un peu de flottement latéral. On teste
   donc la distance PERPENDICULAIRE du feu à l'axe du vent, et non un simple
   écart d'angle. Un cône angulaire fixe de 50° couvrirait ±87 km de côté
   à 100 km de distance : rien que des fausses alertes.
--------------------------------------------------------------------------- */

/** Au-delà, la fumée est trop diluée pour fonder une décision. */
export const PLUME_MAX_KM = 200;
/** Évasement latéral, environ tan(10°). */
export const PLUME_SPREAD = 0.18;
/** Flottement du vent, présent même tout près du foyer. */
export const PLUME_BASE_KM = 2;
/**
 * Sous cette vitesse, la direction du vent ne veut plus rien dire.
 *
 * Valait 3, ce qui laissait passer un vent de 3 km/h relevé à 1 500 m, à
 * partir duquel l'application annonçait une heure d'arrivée de fumée cinq
 * heures plus tard. À cette vitesse la direction d'un modèle météo est du
 * bruit, et le comparateur strict laissait en plus passer la valeur 3 elle-même.
 */
export const WIND_MIN_KMH = 5;

/** Rayon annoncé à l'utilisateur comme "autour de chez toi". */
export const NEARBY_KM = 100;

/* ---------------------------------------------------------------------------
   Incertitude, et refus de la fausse précision

   Un modèle météo donne une direction moyenne, horaire, sur une maille de
   plusieurs kilomètres. L'erreur courante est de 10 à 30 degrés. Afficher une
   heure d'arrivée à la minute, cinq heures à l'avance, revient à inventer une
   précision qui n'existe pas, et c'est le genre de détail qui détruit la
   confiance dès qu'un utilisateur constate l'écart.
--------------------------------------------------------------------------- */

/** Incertitude retenue sur la direction fournie par le modèle météo. */
export const WIND_DIRECTION_UNCERTAINTY_DEG = 20;

/** En dessous, aucune heure d'arrivée n'est annonçable. */
export const ARRIVAL_MIN_SPEED_KMH = 8;

/** Au-delà, le vent aura changé plusieurs fois : on n'annonce plus d'heure. */
export const ARRIVAL_MAX_HOURS = 2;

/** Les heures annoncées sont arrondies à ce pas, en minutes. */
export const ARRIVAL_ROUNDING_MIN = 15;

/* ---------------------------------------------------------------------------
   Pertinence d'une mesure, et proximité d'un feu

   C'est le correctif le plus important de l'application.

   Un capteur situé à 24 km ne renseigne pas sur l'air d'un salon quand un feu
   brûle à 16 km, et le modèle CAMS, de maille voisine de 10 km, ne voit pas
   davantage un panache frais. Le code traitait pourtant ces mesures comme
   représentatives, et concluait qu'on pouvait ouvrir sa fenêtre.

   Un plancher de proximité s'applique donc, que seule une VRAIE mesure locale
   peut lever.
--------------------------------------------------------------------------- */

/** Au-delà, un capteur n'est plus représentatif de l'air qu'on respire. */
export const SENSOR_TRUST_KM = 10;

/**
 * Nombre de capteurs proches et frais pour parler de mesure locale.
 *
 * Ce seuil sert à deux choses, et volontairement au même niveau : décider
 * qu'une valeur est assez étayée pour ne pas être dégradée, et décider qu'elle
 * peut lever un plancher de prudence dû à un feu voisin.
 *
 * Il vaut 3 et non 2. La seconde décision est la plus lourde de conséquences :
 * elle autorise à contredire la géométrie d'un panache. Il serait absurde
 * d'exiger moins de preuves pour cela que pour afficher un chiffre.
 *
 * Conséquence assumée : dans une zone rurale sans capteur, un plancher de
 * prudence ne se lève jamais. C'est le bon comportement, puisque ce sont
 * précisément les endroits où l'on ne peut rien mesurer et où les forêts brûlent.
 */
export const LOCAL_MEASUREMENT_MIN_SENSORS = 3;

/**
 * Un feu susceptible de nous atteindre, plus près que cela : air chargé.
 *
 * 25 km, et non 15. À cette distance un panache porté par un vent de 20 km/h
 * arrive en une heure et quart, la demi-largeur incertaine dépasse déjà 15 km,
 * et aucun réseau de capteurs citoyens ne couvre le rayon de façon fiable.
 *
 * Le cas qui a fixé cette valeur : depuis Mios, Cestas est à 16 km et Lanton à
 * 18 km. Avec un seuil à 15 km, les deux passaient dans la catégorie douce.
 */
export const FIRE_CLOSE_KM = 25;

/** Un feu susceptible de nous atteindre, plus près que cela : prudence. */
export const FIRE_REACHING_KM = 40;

/**
 * Un feu plus près que cela compte quelle que soit la direction du vent.
 *
 * À cette distance une rotation du vent le ramène sur nous en quelques
 * minutes, et l'incertitude de direction interdit d'affirmer qu'il est
 * hors de l'axe.
 */
export const FIRE_ANY_DIRECTION_KM = 10;

/**
 * Largeur du cône qui définit un même régime de vent.
 *
 * On remonte l'historique horaire tant que la direction reste à ±40° de la
 * direction actuelle. Le premier point qui en sort marque le moment où le
 * vent a tourné, et c'est de là que part le calcul du trajet de la fumée.
 */
export const WIND_SHIFT_CONE_DEG = 40;

/* ---------------------------------------------------------------------------
   Regroupement des détections
--------------------------------------------------------------------------- */

/** Deux taches chaudes plus proches que cela appartiennent au même foyer. */
export const ZONE_LINK_KM = 6;
/** Rayon minimal affiché, même pour une zone réduite à un seul pixel. */
export const ZONE_MIN_RADIUS_KM = 1.2;
/** Marge ajoutée au rayon mesuré, pour couvrir l'imprécision du pixel. */
export const ZONE_RADIUS_MARGIN_KM = 0.8;
/** Plafond de zones conservées, les plus étendues d'abord. */
export const MAX_ZONES = 80;

/* ---------------------------------------------------------------------------
   Empreinte au sol des pixels satellite

   Ces trois valeurs ne sont pas des réglages : ce sont les résolutions
   annoncées par les instruments. Elles servent à tracer le contour d'un foyer
   comme l'union des empreintes réellement observées, au lieu d'une enveloppe
   convexe qui comblait les creux.
--------------------------------------------------------------------------- */

/** Résolution des bandes de détection de feu de VIIRS, sur NOAA-20 et SNPP. */
export const FOOTPRINT_VIIRS_KM = 0.375;

/** Résolution des bandes thermiques de MODIS, sur Terra et Aqua. */
export const FOOTPRINT_MODIS_KM = 1;

/**
 * Empreinte retenue quand la source est inconnue.
 *
 * On prend la plus grossière des deux. Sous-estimer une empreinte revient à
 * dessiner un feu plus petit qu'il n'a été mesuré, ce qui va dans le mauvais
 * sens pour une application de prudence.
 */
export const FOOTPRINT_DEFAULT_KM = FOOTPRINT_MODIS_KM;

/**
 * Empreinte d'un pixel du balayage d'images.
 *
 * Déduite de la géométrie réelle des tuiles, pas choisie : la grille fixe donne
 * environ 842 m par pixel en mètres projetés, et un mètre projeté en Mercator
 * vaut cos(latitude) mètre au sol. Sur la France, cela donne de 0,53 km au nord
 * à 0,64 km au sud.
 *
 * On retient la plus grossière. Elle surestime donc légèrement dans le nord, ce
 * qui est le seul sens acceptable pour une application de prudence.
 */
export const FOOTPRINT_IMAGE_SCAN_KM = 0.65;

/**
 * Pas de la grille sur laquelle les empreintes sont unies.
 *
 * Égal à la plus fine des résolutions : une détection VIIRS occupe donc une
 * cellule, une détection MODIS un bloc de trois par trois. Prendre plus fin
 * n'apporterait aucune information, l'instrument n'en fournit pas davantage.
 */
export const FOOTPRINT_GRID_KM = FOOTPRINT_VIIRS_KM;

/**
 * Plafond d'anneaux dessinés par zone.
 *
 * Un foyer très fragmenté peut produire des dizaines d'îlots. Les plus vastes
 * sont conservés, et le plafond est déclaré ici plutôt que subi : une troncature
 * silencieuse se lirait comme une couverture complète.
 */
export const ZONE_MAX_OUTLINE_RINGS = 24;

/* ---------------------------------------------------------------------------
   Surfaces brûlées EFFIS Copernicus

   À ne jamais confondre avec les zones de feu. VIIRS dit ce qui est chaud
   maintenant, EFFIS dit ce qui a brûlé, souvent plusieurs jours plus tôt. Ce
   qui a brûlé ne fume plus, donc ces surfaces n'entrent pas dans le verdict.
   Voir src/core/burnt.ts.
--------------------------------------------------------------------------- */

/**
 * Service WFS d'EFFIS.
 *
 * Il envoie `Access-Control-Allow-Origin: *`, donc l'appel part directement du
 * navigateur, sans fonction serveur. Vérifié le 28 juillet 2026, contrairement à
 * ce qu'affirmait une note antérieure du projet.
 */
export const EFFIS_WFS_URL = 'https://maps.effis.emergency.copernicus.eu/gwis';

/**
 * Couche des périmètres de surfaces brûlées.
 *
 * Le nom ne se devine pas : `modis.ba.poly`, cité par plusieurs exemples en
 * ligne, ne répond pas, et `GetCapabilities` n'aboutit jamais sur ce serveur.
 * Celui-ci a été trouvé en interrogeant le service.
 */
export const EFFIS_LAYER = 'nrt.ba.poly';

/** Profondeur de l'historique demandé. Quatorze jours pèsent environ 110 ko. */
export const BURNT_DAYS_BACK = 14;

/** Plafond de surfaces conservées, les plus vastes d'abord. */
export const BURNT_MAX_AREAS = 120;

/**
 * Débordement toléré autour du contour de la France.
 *
 * Sert à déduire l'ordre des axes, pas à filtrer géographiquement. Un incendie
 * transfrontalier doit rester accepté.
 */
export const BURNT_COORD_MARGIN_DEG = 0.5;

/**
 * En dessous de ce nombre de taches, le signal est faible.
 *
 * L'ordre de grandeur compte. L'incendie du massif landais donnait 642 taches,
 * une détection isolée près de Blanquefort en donnait 8. L'application les
 * présentait à l'identique, ce qui plaçait un brûlage agricole au même rang
 * qu'un sinistre de 42 000 hectares.
 *
 * Faute d'indice de confiance, l'image PNG de la NASA n'en fournissant aucun,
 * le nombre de taches est le seul substitut disponible.
 */
export const WEAK_DETECTION_PIXELS = 10;

/**
 * En dessous, la confiance annoncée par FIRMS ne suffit pas.
 *
 * VIIRS ne donne que trois niveaux, ramenés ici à 20, 60 et 90. Le seuil de 40
 * écarte donc le niveau bas et garde les deux autres.
 */
export const WEAK_CONFIDENCE = 40;

/**
 * En dessous, la puissance radiative relève du brûlage, pas du sinistre.
 *
 * Un feu de broussailles rayonne quelques mégawatts, un front de flammes
 * plusieurs centaines. C'est la mesure qui manquait le plus à l'image PNG.
 */
export const WEAK_FRP_MW = 5;
/**
 * Nombre de zones les plus proches dont on cherche le nom de commune.
 *
 * Valait 12, ce qui laissait une cinquantaine de polygones anonymes sur la
 * carte un jour chargé. Les requêtes partent désormais en parallèle et le
 * résultat est mis en cache, donc le coût réel est faible : après le premier
 * balayage, seules les zones nouvelles déclenchent un appel.
 */
export const NAMED_ZONES = 40;

/** Requêtes de nommage simultanées. Au-delà, on martèle geo.api.gouv.fr. */
export const NAMING_PARALLEL = 4;

/* ---------------------------------------------------------------------------
   Balayage satellite

   La grille est FIXE et couvre toute la France, jamais la fenêtre affichée.
   Sinon la résolution change à chaque zoom et les zones se redessinent
   différemment à chaque déplacement de la carte.

   2×2 tuiles de 1024 px couvrent la France à environ 840 m par pixel, soit
   8 requêtes sur deux jours au lieu de 27 pour une résolution équivalente.
   Le coût est dominé par la latence de chaque requête et non par leur
   taille : mieux vaut peu d'images larges que beaucoup de petites.
--------------------------------------------------------------------------- */

export const SCAN_GRID = 2;
export const SCAN_TILE_PX = 1024;
/** Nombre de jours antérieurs cumulés, pour couvrir les feux qui durent. */
export const SCAN_DAYS_BACK = 1;
/** Au-delà, les décodages d'images échouent par saturation mémoire. */
export const SCAN_MAX_PARALLEL = 6;
/** Reprises complémentaires quand la totalité du balayage a échoué. */
export const SCAN_MAX_RETRIES = 2;

/* ---------------------------------------------------------------------------
   Carte
--------------------------------------------------------------------------- */

export const MAP_MIN_ZOOM = 5;
export const MAP_MAX_ZOOM = 17;
/** En dessous, les noms de commune se chevauchent et rendent la carte illisible. */
export const MAP_LABEL_MIN_ZOOM = 8;
